'use client';

import { FeatureFlag, getFeatureFlag } from '@/lib/config/features';
import { AuthService, AuthError, AuthErrorType } from '@/lib/services/auth-service';
import { createBrowserClient } from '@/lib/supabase-clients';
import { AuthMonitoring } from '@/lib/auth/monitoring';

/**
 * Error types that are recoverable using the legacy auth system
 */
const RECOVERABLE_ERROR_TYPES = [
  AuthErrorType.AUTHENTICATION_ERROR,
  AuthErrorType.PROFILE_ERROR,
  AuthErrorType.NETWORK_ERROR,
  AuthErrorType.SERVER_ERROR
];

/**
 * Maximum number of fallback attempts to prevent infinite loops
 */
const MAX_FALLBACK_ATTEMPTS = 3;

/**
 * Maximum number of fallback attempts in a short time window
 */
const MAX_RAPID_FALLBACK_ATTEMPTS = 5;

/**
 * Time window for rapid fallback attempts in milliseconds (1 minute)
 */
const RAPID_FALLBACK_WINDOW = 60 * 1000;

/**
 * Tracks the number of fallback attempts to prevent infinite loops
 */
let fallbackAttempts = 0;

/**
 * Tracks the timestamps of recent fallback attempts
 */
const recentFallbackAttempts: number[] = [];

/**
 * Counter for successful fallbacks for telemetry
 */
let successfulFallbackCount = 0;

/**
 * Stores recovery approaches for different error types
 */
const recoveryApproaches: Record<AuthErrorType, Array<() => Promise<boolean>>> = {
  [AuthErrorType.AUTHENTICATION_ERROR]: [
    // First try refreshing the session
    async () => {
      try {
        const supabase = createBrowserClient();
        const { data, error } = await supabase.auth.refreshSession();
        return !!data.session && !error;
      } catch (e) {
        return false;
      }
    },
    // Then try getting the session directly
    async () => {
      try {
        const supabase = createBrowserClient();
        const { data, error } = await supabase.auth.getSession();
        return !!data.session && !error;
      } catch (e) {
        return false;
      }
    }
  ],
  [AuthErrorType.PROFILE_ERROR]: [
    // For profile errors, try direct API call
    async () => {
      try {
        const response = await fetch('/api/auth/profile');
        if (!response.ok) return false;
        const data = await response.json();
        return !!data.profile;
      } catch (e) {
        return false;
      }
    }
  ],
  [AuthErrorType.NETWORK_ERROR]: [
    // For network errors, retry after a short delay
    async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1500));
        const supabase = createBrowserClient();
        const { data, error } = await supabase.auth.getSession();
        return !!data.session && !error;
      } catch (e) {
        return false;
      }
    }
  ],
  [AuthErrorType.SERVER_ERROR]: [
    // For server errors, try direct approach
    async () => {
      try {
        const supabase = createBrowserClient();
        const { data, error } = await supabase.auth.getSession();
        return !!data.session && !error;
      } catch (e) {
        return false;
      }
    }
  ],
  [AuthErrorType.UNKNOWN_ERROR]: [
    // For unknown errors, try a few different approaches
    async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const supabase = createBrowserClient();
        const { data, error } = await supabase.auth.getSession();
        return !!data.session && !error;
      } catch (e) {
        return false;
      }
    }
  ]
};

/**
 * Utility function to check if we're having too many fallback attempts in a short time
 */
const isFallbackRateLimited = (): boolean => {
  const now = Date.now();
  
  // Remove attempts older than the window
  while (recentFallbackAttempts.length > 0 && recentFallbackAttempts[0] < now - RAPID_FALLBACK_WINDOW) {
    recentFallbackAttempts.shift();
  }
  
  // Check if we have too many recent attempts
  return recentFallbackAttempts.length >= MAX_RAPID_FALLBACK_ATTEMPTS;
};

/**
 * Track a fallback attempt timestamp
 */
const trackFallbackAttempt = (): void => {
  recentFallbackAttempts.push(Date.now());
  fallbackAttempts++;
};

/**
 * Auth fallback handler for critical errors
 * This provides a seamless fallback to the legacy auth system when critical errors occur
 */
export const AuthFallbackHandler = {
  /**
   * Reset the fallback attempt counter
   * Call this periodically to allow new fallback attempts after successful auth
   */
  resetFallbackAttempts: () => {
    fallbackAttempts = 0;
    // Keep only very recent attempts (last 10 seconds)
    const now = Date.now();
    while (recentFallbackAttempts.length > 0 && recentFallbackAttempts[0] < now - 10000) {
      recentFallbackAttempts.shift();
    }
  },
  
  /**
   * Get telemetry data for the fallback system
   */
  getTelemetry: () => {
    return {
      fallbackAttempts,
      successfulFallbackCount,
      recentAttempts: recentFallbackAttempts.length,
      maxAttemptsReached: fallbackAttempts >= MAX_FALLBACK_ATTEMPTS,
      isRateLimited: isFallbackRateLimited()
    };
  },
  
  /**
   * Try to recover from an auth error using multiple approaches
   * @param error The error to recover from
   * @returns Whether recovery was successful
   */
  tryRecover: async (error: AuthError): Promise<boolean> => {
    // Track the error in monitoring system
    AuthMonitoring.trackError(error, false);
    
    // Check if fallback handler is enabled
    if (!getFeatureFlag(FeatureFlag.USE_FALLBACK_HANDLER)) {
      console.log('[FallbackHandler] Fallback handler is disabled');
      return false;
    }
    
    // Check for rate limiting
    if (isFallbackRateLimited()) {
      console.warn('[FallbackHandler] Rate limited - too many fallback attempts in a short time');
      return false;
    }
    
    // Check if we've exceeded the maximum number of fallback attempts
    if (fallbackAttempts >= MAX_FALLBACK_ATTEMPTS) {
      console.warn('[FallbackHandler] Maximum fallback attempts reached');
      return false;
    }
    
    // Check if this error type is recoverable
    if (!RECOVERABLE_ERROR_TYPES.includes(error.type)) {
      console.log(`[FallbackHandler] Error type ${error.type} is not recoverable`);
      return false;
    }
    
    console.log('[FallbackHandler] Attempting recovery for error type:', error.type);
    trackFallbackAttempt();
    
    // Track the fallback attempt
    AuthMonitoring.trackFallback(false);
    
    try {
      // Try error-specific recovery approaches first
      const approaches = recoveryApproaches[error.type] || [];
      
      // Try each approach in sequence
      for (const approach of approaches) {
        try {
          console.log('[FallbackHandler] Trying specialized recovery approach');
          const success = await approach();
          if (success) {
            console.log('[FallbackHandler] Specialized recovery approach succeeded');
            successfulFallbackCount++;
            
            // Track successful fallback and recovery
            AuthMonitoring.trackFallback(true);
            AuthMonitoring.trackRecovery('specialized');
            
            // Notify the system via localStorage
            if (typeof window !== 'undefined') {
              window.localStorage.setItem('auth_fallback_recovery', Date.now().toString());
              window.dispatchEvent(new Event('auth_fallback_recovery'));
            }
            
            return true;
          }
        } catch (e) {
          console.warn('[FallbackHandler] Error in specialized recovery approach:', e);
          // Continue to next approach
        }
      }
      
      // If specialized approaches failed, try legacy auth system directly
      const supabase = createBrowserClient();
      const { data } = await supabase.auth.getSession();
      
      if (data.session) {
        console.log('[FallbackHandler] Successfully recovered session using legacy auth');
        successfulFallbackCount++;
        
        // Track successful fallback and recovery
        AuthMonitoring.trackFallback(true);
        AuthMonitoring.trackRecovery('fallback');
        
        // Notify the improved system via localStorage
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('auth_fallback_recovery', Date.now().toString());
          window.dispatchEvent(new Event('auth_fallback_recovery'));
        }
        
        return true;
      } else {
        console.warn('[FallbackHandler] Legacy auth recovery failed - no session');
        // Update fallback metrics for failed attempt
        AuthMonitoring.trackFallback(false);
        return false;
      }
    } catch (recoveryError) {
      console.error('[FallbackHandler] Error during recovery process:', recoveryError);
      return false;
    }
  },
  
  /**
   * Verify auth state and perform proactive recovery if needed
   * This can be called periodically to ensure auth state is healthy
   */
  verifyAndRecoverAuthState: async (): Promise<boolean> => {
    try {
      // First check if we have a session
      const supabase = createBrowserClient();
      const { data, error } = await supabase.auth.getSession();
      
      if (error || !data.session) {
        console.log('[FallbackHandler] No valid session during verification, attempting recovery');
        
        // Try to recover
        return await AuthFallbackHandler.tryRecover({
          type: AuthErrorType.AUTHENTICATION_ERROR,
          message: 'Session verification failed during routine check',
          originalError: error
        });
      }
      
      return true;
    } catch (e) {
      console.error('[FallbackHandler] Error during auth state verification:', e);
      return false;
    }
  },
  
  /**
   * Register fallback listeners
   * This should be called during app initialization
   */
  registerFallbackListeners: () => {
    // Reset fallback attempts periodically
    const resetInterval = setInterval(() => {
      AuthFallbackHandler.resetFallbackAttempts();
    }, 30 * 60 * 1000); // Reset every 30 minutes
    
    // Periodically verify auth state
    const verifyInterval = setInterval(() => {
      // Only verify if the page is visible
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        AuthFallbackHandler.verifyAndRecoverAuthState().catch(e => {
          console.warn('[FallbackHandler] Error in periodic auth verification:', e);
        });
      }
    }, 10 * 60 * 1000); // Check every 10 minutes
    
    // Listen for auth events
    if (typeof window !== 'undefined') {
      // Successful auth should reset fallback attempts
      window.addEventListener('auth_bridge_login', () => {
        AuthFallbackHandler.resetFallbackAttempts();
      });
      
      // Listen for fallback recovery events from other tabs
      window.addEventListener('storage', (event) => {
        if (event.key === 'auth_fallback_recovery') {
          // Force an auth refresh in the improved system
          AuthService.refreshSession().catch(e => {
            console.warn('[FallbackHandler] Error refreshing session after recovery:', e);
          });
        }
      });
      
      // Listen for visibility changes
      document.addEventListener('visibilitychange', () => {
        // When the page becomes visible after being hidden, verify auth state
        if (document.visibilityState === 'visible') {
          AuthFallbackHandler.verifyAndRecoverAuthState().catch(e => {
            console.warn('[FallbackHandler] Error in visibility-triggered auth verification:', e);
          });
        }
      });
    }
    
    // Return cleanup function
    return () => {
      clearInterval(resetInterval);
      clearInterval(verifyInterval);
      
      if (typeof window !== 'undefined') {
        window.removeEventListener('auth_bridge_login', AuthFallbackHandler.resetFallbackAttempts);
        document.removeEventListener('visibilitychange', () => {});
      }
    };
  }
};